import { Component, ViewContainerRef } from '@angular/core';
import { TosTableComponent } from '../../tos-table/tos-table.component';
import { AdvancedFilterComponentV2 } from '../../advanced-filter/advanced-filter-v2.component';
import { TableMenuOption, TableMeta } from '../../../models/table-meta.interface';
import { StatsData } from '../../../models/stats-response.interface';
import { PaginatedListDataResponse } from '../../../models/paginated-list-data-response';
import { TableListRequest, Where } from '../../../models/table-list-request.interface';
import { ToasterService } from '../../../services/toaster.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../../services/loading.service';
import paymentConfig from './config/payment-config.json';
import { forkJoin } from 'rxjs';
import { CommonUtils } from '../../../utils/common-utils';
import { ModalComponent } from '../../modal/modal.component';
import { AdvancedFilterFieldInfo } from '../../../models/advanced-filter-config.interface';
import { FinanceApiService } from '../../../services/finance-a-p-iervice.service';
import { SharedModule } from '../../../modules/shared/shared.module';
import { SvgIconComponent } from '../../svg-icon/svg-icon.component';
import { ApiService } from '../../../services/api.service';
import { CommonService } from '../../../services/common-service.service';
import PaymentConfig from "./config/payment-config.json";

@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [TosTableComponent, SharedModule, SvgIconComponent],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss'
})
export class PaymentComponent {
  tableLoaded: boolean = false;
  tableMeta!: TableMeta;
  totalTablePages = 0;
  currentTablePage = 1;
  currentPage: number = 0;
  rowsPerPage: number = 15;
  rowsPerPageOptions: number[] = [];
  focusSearch: boolean = false;
  searchTerm: string = '';
  previousTerm: string = '';
  selectedItems: any[] = [];
  // deSelectedItems: User[] = [];
  sortColumn: string = 'createdDate';
  sortDirection: 'asc' | 'desc' = 'desc';
  allTableRecordsSelected: boolean = false;
  tableStats: StatsData = {
    total: 0,
    active: 0,
    inactive: 0,
  };
  tableListData: PaginatedListDataResponse = {
    totalCount: 0,
    page: 0,
    limit: 0,
    records: [],
  };
  tableListRequest: TableListRequest = {
    orderBy: {
      [this.sortColumn]: this.sortDirection,
    },
    includeTotal: true,
    limit: this.rowsPerPage,
    page: this.currentPage,
  };

  filterData: Where | null = null;
  rawFilterData: Where | null = null;
  searchFilterData!: Where | null;

  // table menu
  openMenuIndex: number | null = null;
  menuOptions: TableMenuOption[] = [
    { label: 'View', value: 'view' },
    { label: 'Download Attachments', value: 'downloadAttachments', condition: (record: any) => record.fileIds && record.fileIds.length > 0}
  ];


  isFilterApplied: boolean = false;

  protected readonly PaymentConfig = paymentConfig;

  constructor(
    private toasterService: ToasterService,
    private viewContainerRef: ViewContainerRef,
    private router: Router,
    private route: ActivatedRoute,
    private loadingService: LoadingService,
    private financeApi: FinanceApiService,
    private api: ApiService,
    private commonService: CommonService
  ) {
    this.tableMeta = paymentConfig.tableConfig;
  }

  ngOnInit() {
    this.tableLoaded = false;
    this.getTableList();
  }

  // pagination helper methods
  patchTableListWithSerialNumbers() {
    this.tableListData.records?.forEach((record: any, index: number) => {
      record.slNo = (this.currentTablePage - 1) * this.rowsPerPage + index + 1;
    });
  }

  calculateTotalPages() {
    if (this.tableListRequest.where) {
      this.totalTablePages = Math.ceil(
        this.tableListData.totalCount / this.rowsPerPage
      );
    } else {
      this.totalTablePages = Math.ceil(
        this.tableStats.total / this.rowsPerPage
      );
    }
  }

  generateRowsPerPageOptions(totalRecords: number): void {
    const baseOptions = [10];
    const dynamicOptions = [];

    if (totalRecords > 10 && totalRecords <= 25) {
      dynamicOptions.push(25);
    }

    if (totalRecords > 25 && totalRecords <= 50) {
      dynamicOptions.push(25, 50);
    }

    // If totalRecords is between 1 and 100, add 100 to the options
    if (totalRecords > 50 && totalRecords <= 100) {
      dynamicOptions.push(50, 100);
    }

    // If totalRecords is between 101 and 1000, add 150, 250, 500, 1000
    if (totalRecords > 100 && totalRecords <= 1000) {
      dynamicOptions.push(100, 150, 250, 500, 1000);
    }

    // If totalRecords is greater than 1000, add multiples of 500
    if (totalRecords > 1000) {
      dynamicOptions.push(100);
      let nextOption = 150;

      // Add options starting from 150, 250, 500, 1000
      while (nextOption <= 1000) {
        dynamicOptions.push(nextOption);
        nextOption += nextOption === 150 ? 100 : 250; // add 100, then 250 up to 1000
      }

      // After 1000, add multiples of 500
      nextOption = 1500;
      while (nextOption <= totalRecords) {
        dynamicOptions.push(nextOption);
        nextOption += 500;
      }
    }

    this.rowsPerPageOptions = [...new Set([...baseOptions, ...dynamicOptions])];
  }

  updateRowsPerPage(newRowsPerPage: number) {
    this.rowsPerPage = newRowsPerPage;
    this.tableListRequest.limit = newRowsPerPage;
    this.tableListRequest.page = 0;
    this.currentTablePage = 1; // Reset to first page
    this.calculateTotalPages();
    this.getTableList();
  }

  goToPage(page: number) {
    this.currentTablePage = page;
    this.tableListRequest.page = this.currentTablePage - 1;
    this.getTableList();
  }

  nextPage() {
    if (this.currentTablePage < this.totalTablePages) {
      this.currentTablePage++;
      this.tableListRequest.page = this.currentTablePage - 1;
      this.getTableList();
    }
  }

  previousPage() {
    if (this.currentTablePage > 1) {
      this.currentTablePage--;
      this.tableListRequest.page = this.currentTablePage - 1;
      this.getTableList();
    }
  }

  sortData(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.tableListRequest.orderBy = {
      [this.sortColumn]: this.sortDirection,
    };
    this.refreshTableList();
  }

  // filter() {
  //   // console.log(this.customerMasterListRequest);
  //   const modalRef = this.viewContainerRef.createComponent(ModalComponent);
  //   modalRef.instance.title = 'Advanced Filter';
  //   const filterDialogRef = modalRef.instance.loadComponent(
  //     AdvancedFilterComponentV2
  //   );
  //
  //   const fieldInfoList: AdvancedFilterFieldInfo[] = CommonUtils.getFilterFieldList(paymentConfig.filterConfig);
  //   filterDialogRef.setInput("filterFields", fieldInfoList);
  //   filterDialogRef.setInput("useNewContract", true);
  //   filterDialogRef.setInput("appliedFilters", this.rawFilterData);
  //
  //   modalRef.instance.close.subscribe((res: any) => {
  //     this.clearSelection();
  //     if (res && res.resetAll) {
  //       this.rawFilterData = null;
  //       this.filterData = null;
  //       this.isFilterApplied = false;
  //       this.refreshTableList();
  //     } else if (res && res.refresh) {
  //       if (
  //         res.filterData.expression &&
  //         res.filterData.filterConditions.length > 0
  //       ) {
  //         this.filterData = {
  //           filterConditions: res.filterData.filterConditions,
  //           expression: res.filterData.expression,
  //         };
  //         this.isFilterApplied = true;
  //       } else {
  //         console.error('Invalid filter !');
  //         this.rawFilterData = null;
  //         this.filterData = null;
  //         this.isFilterApplied = false;
  //       }
  //       this.refreshTableList();
  //     }
  //     modalRef.destroy();
  //   });
  // }

  getTableList() {
    this.loadingService.show('inline');
    // this.mergeFilters();
    this.financeApi.getPaymentList(this.tableListRequest).subscribe({
      next: (res) => {
        this.tableListData = res.data;
        this.patchTableListWithSerialNumbers();
        this.calculateTotalPages();
        this.generateRowsPerPageOptions(res.data.totalCount);
        this.loadingService.hide();
      },
      error: (err) => {
        console.error(err);
        this.loadingService.hide();
      },
    });
  }

  mergeFilters() {
    delete this.tableListRequest.where;
    console.log("this.filterData: ", this.filterData);
    if (!this.searchFilterData && !this.filterData) return;

    if (!this.tableListRequest.where) {
      this.tableListRequest.where = {filterConditions: [], expression: ''};
    }

    if (this.filterData?.filterConditions?.length) {
      this.filterData.filterConditions.forEach((filter: any) => {
        this.tableListRequest.where?.filterConditions?.push(filter);
      });
    }

    if (this.searchFilterData?.filterConditions?.length) {
      this.searchFilterData.filterConditions.forEach((filter: any) => {
        this.tableListRequest.where?.filterConditions?.push(filter);
      });
    }

    // Merge expressions with proper checks
    const expressions: string[] = [];
    if (this.filterData?.expression) {
      expressions.push(`(${this.filterData.expression})`);
    }
    if (this.searchFilterData?.expression) {
      expressions.push(`(${this.searchFilterData.expression})`);
    }

    // Safely assign the joined expression
    this.tableListRequest.where.expression = expressions.join(' AND ');
  }

  handleTableSearch(event: KeyboardEvent) {
    const searchTerm = this.searchTerm.trim();
    if (searchTerm.length >= 3) {
      this.searchFilterData = {
        filterConditions: [
          {
            name: "CUSTOMER_NAME_CONTAINS",
            alias: "customerName",
            value: [searchTerm]
          }
        ],
        expression: "customerName"
      };
      // Search when Enter is pressed
      if (event.key === 'Enter') {
        this.getTableList();
      }
    }
    // Call reset only when the search bar is cleared (avoid on multiple backspaces)
    if (searchTerm.length === 0 && this.previousTerm.length !== 0) {
      this.searchFilterData = null;
      this.getTableList();
    }
    this.previousTerm = searchTerm;
  }

  handleMenuToggle(index: number) {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  handleMenuOptionSelection(event: {record: any, actionName: string}) {
    this.handleOptionSelected(event.record, event.actionName)
  }

  handleOptionSelected(record: any, actionName: string) {
    switch (actionName) {
      case 'view':
        this.router.navigate([`view/${record.id}`], {
          relativeTo: this.route,
          state: { recordPaymentInput: {redirectPath: '/manage-finance/receipts'},
               previousState: null}
        });
        break;
      case 'downloadAttachments':
        this.commonService.downloadFilesAsZip(record.fileIds);
        break;
      default:
        console.warn('Unknown action:', actionName);
    }
  }

  clearSelection() {
    this.selectedItems = [];
  }

  clearSearchQuery() {
    this.searchTerm = '';
  }

  refreshTableList() {
    this.getTableList();
  }

  recordPayment(advancePayment: boolean) {
    this.router.navigate([`record-payment`], {
      relativeTo: this.route,
      state: { recordPaymentInput: {isAdvancePayment: advancePayment, redirectPath: '/manage-finance/receipts'},
               previousState: null}
    });
  }
}
